import * as React from 'react';
import { observe } from 'mobx';
import { inject, observer } from 'mobx-react';
import { Button, Form, Modal } from 'components/antd';
import {
	BookingDictionariesStore,
	NewBookingsStore,
	PassengerDetailsStore,
	RootStore,
	BookingCCStore,
} from 'AppClient/stores';
import PricingBooking from 'AppClient/components/PricingBooking';
import BookBooking from 'AppClient/components/BookBooking';
import NewBookingRegards from 'AppClient/components/NewBookingRegards';
import Provider from './Provider';
import classNames from 'classnames';
import { ThemeContext } from 'app-themes';
import { scrollModalToTop } from 'utils/utils';

import styles from './styles.module.scss';

interface Props {
	bookingCCStore?: BookingCCStore;
	newBookingsStore?: NewBookingsStore;
	bookingDictionariesStore?: BookingDictionariesStore;
	passengerDetailsStore?: PassengerDetailsStore;
}
interface ButtonConfig {
	handler?: () => void;
	name?: string;
	disabled?: boolean;
}
@inject(
	({
		newBookingsStore,
		bookingDictionariesStore,
		passengerDetailsStore,
		bookingCCStore,
	}: RootStore) => ({
		newBookingsStore,
		bookingDictionariesStore,
		passengerDetailsStore,
		bookingCCStore,
	})
)
@observer
export class CreateBookingModal extends React.Component<Props> {
	static contextType = ThemeContext;

	constructor(props: Props) {
		super(props);
		observe(props.newBookingsStore!, 'isShownCreateBookingModal', ({ newValue }) => {
			if (!newValue) {
				props.newBookingsStore!.clearBookingPricing();
			} else {
				props.newBookingsStore!.prepareStartedPayload();
			}
		});
	}

	componentDidUpdate() {
		const { step } = this.props.newBookingsStore!;

		if (step === 'REGARDS') {
			scrollModalToTop();
		}
	}

	toggleModal = () => {
		this.props.newBookingsStore!.toggleModal();
	};

	createPrices = () => {
		this.props.newBookingsStore!.createPrices();
	};

	bookAnotherJourney = () => {
		this.props.newBookingsStore!.bookAnotherJourney();
	};

	get getModalTitle() {
		const { step, bookedSummary, isEditingUpcomingBooking } = this.props.newBookingsStore!;
		if (isEditingUpcomingBooking && step !== 'REGARDS') {
			return <span>Edit trip</span>;
		}
		const bookingNumber = bookedSummary ? (
			<>
				, <br /> your reservation number is {bookedSummary.bookingNumber}
			</>
		) : null;
		return step === 'REGARDS' ? (
			<span>Thank you for booking with us{bookingNumber}</span>
		) : (
			<span>Create new booking</span>
		);
	}

	get footerButton() {
		const { isBookingCreditCardValid } = this.props.bookingCCStore!;
		const { step, isPricingValid, isLoading } = this.props.newBookingsStore!;
		const { isAmendingDetailsFormValid } = this.props.passengerDetailsStore!;
		let config: ButtonConfig = {
			handler: undefined,
			name: '',
			disabled: undefined,
		};
		if (step === 'REGARDS') {
			config = {
				handler: this.bookAnotherJourney,
				name: 'Book another journey',
				disabled: undefined,
			};
		} else {
			config = {
				handler: this.createPrices,
				name: 'Get quote',
				disabled:
					!isPricingValid || !isAmendingDetailsFormValid || !isBookingCreditCardValid,
			};
		}
		return (
			<div>
				{this.actionButton(isLoading)}
				{step === 'BOOK' ? null : (
					<Button
						size="large"
						type="primary"
						key="action"
						disabled={config.disabled || isLoading}
						onClick={config.handler}
					>
						{config.name}
					</Button>
				)}
			</div>
		);
	}

	actionButton = (isLoading: boolean) => {
		const { step } = this.props.newBookingsStore!;
		return (
			<Button size="large" disabled={isLoading} onClick={this.toggleModal}>
				{step === 'REGARDS' ? 'Close' : 'Cancel'}
			</Button>
		);
	};

	render() {
		const {
			isLoading,
			step,
			isShownCreateBookingModal: visible,
		} = this.props.newBookingsStore!;
		const { isCityFetching } = this.props.bookingDictionariesStore!;
		const isCCPending = this.props.bookingCCStore!.isPending;
		const theme = this.context;

		return (
			<div>
				<Button
					size="large"
					type="primary"
					onClick={this.toggleModal}
					className={classNames({ [styles[`btnHeader-${theme}`]]: theme !== '' })}
				>
					New booking
				</Button>
				{visible ? (
					<Modal
						visible={visible}
						onCancel={this.toggleModal}
						title={this.getModalTitle}
						specificBrowserTabTitle={
							step === 'REGARDS' ? 'Thank you for booking with us' : undefined
						}
						footer={this.footerButton}
						isLoading={isLoading || isCityFetching || isCCPending}
					>
						<Form layout="vertical" className={styles.createBooking}>
							<Provider step="PRICE" component={PricingBooking} />
							<Provider step="EDIT" component={PricingBooking} />
							<Provider step="BOOK" component={BookBooking} />
							<Provider step="REGARDS" component={NewBookingRegards} />
						</Form>
					</Modal>
				) : null}
			</div>
		);
	}
}
